/**
 * Certificate layout maths, shared by the issued-certificate canvas and the
 * template editor's preview.
 *
 * These two used to diverge: the canvas shrank text to fit `widthPct` while the
 * preview did not, so a creator placed fields against a preview that did not
 * match what learners received. Both now measure through `fitFontSize`, so the
 * preview lies only as much as font loading allows.
 *
 * Every size is authored against a 1000px-wide reference design and scaled to
 * whatever width the certificate is actually rendered at.
 */
import type { CertificatePlaceholder } from '@mlm/shared';

/** The design width every `fontSizePx` is authored against. */
export const CERTIFICATE_REFERENCE_WIDTH = 1000;

export const CERTIFICATE_FONT_FAMILY = '"Hanken Grotesk", system-ui, sans-serif';

/** A canvas/`measureText` font string at the given pixel size. */
export function certificateFont(sizePx: number): string {
  return `600 ${sizePx}px ${CERTIFICATE_FONT_FAMILY}`;
}

export type CertificateFieldSpec = {
  value: CertificatePlaceholder['field'];
  label: string;
  sample: string;
};

/** The four placeable fields, with the sample text the editor previews. */
export const CERTIFICATE_FIELDS: readonly CertificateFieldSpec[] = [
  { value: 'member_name', label: 'Learner name', sample: 'Maria Dela Cruz' },
  { value: 'course_title', label: 'Course title', sample: 'Start Your Online Store' },
  { value: 'issued_date', label: 'Date issued', sample: 'August 18, 2026' },
  { value: 'verify_code', label: 'Verification code', sample: '7K3M9PQ2XZ' },
];

export function certificateFieldLabel(field: CertificatePlaceholder['field']): string {
  return CERTIFICATE_FIELDS.find((f) => f.value === field)?.label ?? field;
}

export function certificateFieldSample(field: CertificatePlaceholder['field']): string {
  return CERTIFICATE_FIELDS.find((f) => f.value === field)?.sample ?? '';
}

/**
 * Shrink a font size until the text fits `maxWidthPx`, never below `minPx`.
 * `measure` is injected so the same loop serves a canvas 2D context and a test.
 */
export function fitFontSize(
  measure: (font: string, text: string) => number,
  text: string,
  fontSizePx: number,
  maxWidthPx: number,
  minPx = 8,
): number {
  let size = fontSizePx;
  while (size > minPx && measure(certificateFont(size), text) > maxWidthPx) size -= 1;
  return size;
}

export function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** Arrow-key nudging on the preview. */
export function nudgePlaceholder(
  p: CertificatePlaceholder,
  key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown',
  step: number,
): CertificatePlaceholder {
  switch (key) {
    case 'ArrowLeft':
      return { ...p, xPct: clampPct(p.xPct - step) };
    case 'ArrowRight':
      return { ...p, xPct: clampPct(p.xPct + step) };
    case 'ArrowUp':
      return { ...p, yPct: clampPct(p.yPct - step) };
    case 'ArrowDown':
      return { ...p, yPct: clampPct(p.yPct + step) };
  }
}

/**
 * Fields still selectable for the placeholder at `keepIndex` — every field not
 * already placed elsewhere, plus its own. Placing the same field twice renders
 * the same text on top of itself, which is never what anyone meant.
 */
export function availableFields(
  placeholders: CertificatePlaceholder[],
  keepIndex: number,
): readonly CertificateFieldSpec[] {
  const used = new Set(placeholders.filter((_, i) => i !== keepIndex).map((p) => p.field));
  return CERTIFICATE_FIELDS.filter((f) => !used.has(f.value));
}

/** A sensible new placeholder: the first unused field, mid-page. */
export function defaultPlaceholder(
  placeholders: CertificatePlaceholder[],
): CertificatePlaceholder | null {
  const used = new Set(placeholders.map((p) => p.field));
  const next = CERTIFICATE_FIELDS.find((f) => !used.has(f.value));
  if (!next) return null;
  return {
    field: next.value,
    xPct: 50,
    yPct: 70,
    widthPct: 60,
    fontSizePx: 20,
    color: '#444444',
    align: 'center',
  };
}

/** The first placeholder a brand-new template gets: the learner's name. */
export function initialPlaceholders(): CertificatePlaceholder[] {
  return [
    {
      field: 'member_name',
      xPct: 50,
      yPct: 48,
      widthPct: 80,
      fontSizePx: 48,
      color: '#222222',
      align: 'center',
    },
  ];
}

export type CertificateTemplateEditorProps = {
  /** Changing this resets the draft — one editor instance, many templates. */
  templateId: string;
  name: string;
  imageUrl: string;
  placeholders: CertificatePlaceholder[];
  saving?: boolean;
  deleting?: boolean;
  /** Cap on placed fields (there are four distinct fields). */
  maxPlaceholders?: number;
  /** False hides the background-replace input (a read-only viewer). */
  allowImageReplace?: boolean;
  deleteDescription?: string;
  onSave: (next: {
    name: string;
    config: { placeholders: CertificatePlaceholder[] };
    imageFile: File | null;
  }) => void;
  onDelete: () => void;
};
